import { useState, useRef } from 'react';
import Switch from 'react-switch';
import { Circles } from 'react-loader-spinner';
import AddressField from '../AddressField';
import { createSublocation } from './utils';

const EMPTY_SUBLOCATION = {
    name: '',
    locationId: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    latitude: '',
    longitude: '',
    timezone: '',
    isPreset: false,
    isFinal: false,
    availableFrom: null,
    availableTo: null,
};

const SublocationModal = ({ locations, onCreated, onClose }) => {
    const [sublocation, setSublocation] = useState({ ...EMPTY_SUBLOCATION });
    const [loading, setLoading] = useState(false);
    const backdropRef = useRef(null);

    const handleBackdropClick = (e) => {
        if (e.target === backdropRef.current) onClose();
    };

    const handleFieldChange = (field, value) => {
        setSublocation(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const created = await createSublocation(sublocation);
            setLoading(false);
            if (created?.id) {
                onCreated(created);
                onClose();
            }
        } catch (e) {
            console.error('Failed to create sublocation', e);
            setLoading(false);
        }
    };

    const addressFilled = !!sublocation.address1;
    const canSubmit = sublocation.locationId && addressFilled && sublocation.name;

    return (
        <div className="modal-wrapper" ref={backdropRef} onClick={handleBackdropClick}>
            <div className="modal CreateSublocationModal">
                {loading
                    ? <div className="Loading">
                        <Circles height="80" width="80" color="#2386EE" ariaLabel="circles-loading" visible={true} />
                    </div>
                    : <>
                        <div className="modal-header">
                            <h2>New Sublocation</h2>
                        </div>

                        <div className="modal-body">
                            <div className="input-container">
                                <div className="label">Name:</div>
                                <input
                                    className="input"
                                    placeholder="e.g. Main Entrance"
                                    value={sublocation.name}
                                    onChange={e => handleFieldChange('name', e.target.value)}
                                />
                            </div>

                            <div className="input-container">
                                <div className="label">Location:</div>
                                <select
                                    className="input"
                                    value={sublocation.locationId}
                                    onChange={e => handleFieldChange('locationId', e.target.value)}
                                >
                                    <option value="">Select a location…</option>
                                    {locations.map(loc => (
                                        <option key={loc.id} value={loc.id}>
                                            {loc.name}{loc.address1 ? ` — ${loc.address1}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="input-container">
                                <div className="label">Address:</div>
                                <AddressField
                                    existingLocation={sublocation}
                                    placeholder="Search for an address…"
                                    editLocation={(loc) => setSublocation({
                                        ...loc,
                                        latitude: loc.latitude != null ? parseFloat(loc.latitude.toFixed(6)) : loc.latitude,
                                        longitude: loc.longitude != null ? parseFloat(loc.longitude.toFixed(6)) : loc.longitude,
                                    })}
                                    searchValue=""
                                    setIsLocationEdited={() => {}}
                                />
                            </div>

                            {addressFilled && (
                                <div className="address-confirmation">
                                    <div className="confirm-row"><span className="confirm-label">City:</span> {sublocation.city}</div>
                                    <div className="confirm-row"><span className="confirm-label">State:</span> {sublocation.state}</div>
                                    <div className="confirm-row"><span className="confirm-label">Zip:</span> {sublocation.zip}</div>
                                    <div className="confirm-row"><span className="confirm-label">Country:</span> {sublocation.country}</div>
                                    <div className="confirm-row">
                                        <span className="confirm-label">Lat / Lng:</span>
                                        {sublocation.latitude}, {sublocation.longitude}
                                    </div>
                                    <div className="confirm-row"><span className="confirm-label">Timezone:</span> {sublocation.timezone}</div>
                                </div>
                            )}

                            <div className="input-container">
                                <div className="label">Address 2:</div>
                                <input
                                    className="input"
                                    placeholder="Unit, floor, suite…"
                                    value={sublocation.address2 || ''}
                                    onChange={e => handleFieldChange('address2', e.target.value)}
                                />
                            </div>

                            <div className="toggle-row">
                                <div className="toggle-item">
                                    <span className="label">Is Preset</span>
                                    <Switch
                                        checked={sublocation.isPreset}
                                        onChange={val => handleFieldChange('isPreset', val)}
                                        onColor="#2386EE"
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        height={20}
                                        width={45}
                                    />
                                </div>
                                <div className="toggle-item">
                                    <span className="label">Is Final</span>
                                    <Switch
                                        checked={sublocation.isFinal}
                                        onChange={val => handleFieldChange('isFinal', val)}
                                        onColor="#2386EE"
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        height={20}
                                        width={45}
                                    />
                                </div>
                            </div>

                            <div className="input-container">
                                <div className="label">Available From:</div>
                                <input
                                    className="input"
                                    type="datetime-local"
                                    value={sublocation.availableFrom || ''}
                                    onChange={e => handleFieldChange('availableFrom', e.target.value)}
                                />
                            </div>
                            <div className="input-container">
                                <div className="label">Available To:</div>
                                <input
                                    className="input"
                                    type="datetime-local"
                                    value={sublocation.availableTo || ''}
                                    onChange={e => handleFieldChange('availableTo', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={onClose}>Cancel</button>
                            <button className="create-btn" onClick={handleSubmit} disabled={!canSubmit}>
                                Create
                            </button>
                        </div>
                    </>
                }
            </div>
        </div>
    );
};

export default SublocationModal;
